import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Area, Line, LineChart, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { COL, kwh, n } from "./clienteApi";

const ax = { tick: { fontSize: 10, fill: "#94A3B8" }, axisLine: false, tickLine: false };
const tCol = (t) => (t == null ? "#64748B" : t >= 60 ? "#DC2626" : t >= 50 ? "#F59E0B" : t < 0 ? "#2563EB" : "#10B981");
const tStato = (t) => (t == null ? "—" : t >= 60 ? "molto calda" : t >= 50 ? "calda" : t < 0 ? "sotto zero" : "regolare");

// Popup batteria: carica/scarica (kW), livello di carica (%) e andamento della temperatura di oggi
export function ClienteBatteria({ open, onClose, src, soh, temp }) {
  const [d, setD] = useState(null);
  const [tmp, setTmp] = useState(null);
  useEffect(() => {
    if (!open) return;
    setD(null);
    setTmp(null);
    const day = format(new Date(), "yyyy-MM-dd");
    src.line("day", day).then(setD).catch(() => setD({ points: [], riepilogo: {} }));
    src.tempSerie?.(day).then(setTmp).catch(() => setTmp({ punti: [] }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pts = (d?.points || []).map((p) => ({ t: p.t, carica: p.batt_w != null && p.batt_w < 0 ? -p.batt_w / 1000 : 0, scarica: p.batt_w > 0 ? p.batt_w / 1000 : 0, soc: p.soc ?? null }));
  const r = d?.riepilogo || {};
  const tPts = (tmp?.punti || []).map((p) => ({ t: p.t, temp: p.v }));
  const minT = Math.min(0, ...tPts.map((p) => p.temp));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="cliente-font max-w-md rounded-3xl bg-white text-slate-800" data-testid="cliente-batteria-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-extrabold">La tua batteria oggi</DialogTitle>
          <DialogDescription className="text-xs font-semibold text-slate-400">Carica e scarica in kW · livello di carica in % · temperatura in °C</DialogDescription>
        </DialogHeader>
        {!d ? <div className="flex h-56 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div> : (
          <>
            <div className="h-56" data-testid="cliente-batteria-grafico"><ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={pts}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="t" {...ax} interval="preserveStartEnd" minTickGap={40} />
                <YAxis yAxisId="kw" {...ax} width={30} />
                <YAxis yAxisId="soc" orientation="right" domain={[0, 100]} {...ax} width={30} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v, nm) => [nm === "Livello" ? `${v}%` : `${n(v, 2)} kW`, nm]} />
                <ReferenceLine yAxisId="kw" y={0} stroke="#CBD5E1" />
                <Area yAxisId="kw" type="monotone" dataKey="carica" name="In carica" stroke={COL.batt} fill={COL.batt} fillOpacity={0.3} strokeWidth={1.6} dot={false} />
                <Area yAxisId="kw" type="monotone" dataKey="scarica" name="In scarica" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.25} strokeWidth={1.6} dot={false} />
                <Line yAxisId="soc" type="stepAfter" dataKey="soc" name="Livello" stroke="#0F172A" strokeWidth={1.6} dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer></div>
            <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
              <div className="rounded-2xl bg-lime-50 p-2" data-testid="cliente-batteria-caricata"><b className="block text-sm">{kwh(r.carica)}</b><span className="text-[11px] text-slate-500">caricata</span></div>
              <div className="rounded-2xl bg-amber-50 p-2" data-testid="cliente-batteria-scaricata"><b className="block text-sm">{kwh(r.scarica)}</b><span className="text-[11px] text-slate-500">scaricata</span></div>
              <div className="rounded-2xl bg-slate-50 p-2" data-testid="cliente-batteria-salute"><b className="block text-sm">{soh != null ? `${n(soh, 0)}%` : "—"}</b><span className="text-[11px] text-slate-500">salute</span></div>
              <div className="rounded-2xl p-2" style={{ background: `${tCol(temp)}1A` }} data-testid="cliente-batteria-temperatura">
                <b className="block text-sm" style={{ color: tCol(temp) }}>{temp != null ? `${n(temp, 0)}°C` : "—"}</b>
                <span className="text-[11px] text-slate-500">{temp != null ? tStato(temp) : "temperatura"}</span>
              </div>
            </div>
            {tPts.length >= 2 ? (
              <div className="mt-3" data-testid="cliente-batteria-temp-andamento">
                <div className="mb-1 text-xs font-bold text-slate-600">Temperatura della batteria oggi</div>
                <div className="h-36"><ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tPts}>
                    <CartesianGrid stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="t" {...ax} interval="preserveStartEnd" minTickGap={40} />
                    <YAxis {...ax} width={34} domain={[Math.min(minT, 10), "auto"]} unit="°" />
                    <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v) => [`${n(v, 1)} °C`, "Temperatura"]} />
                    <ReferenceLine y={50} stroke="#F59E0B" strokeDasharray="4 4" />
                    <ReferenceLine y={60} stroke="#DC2626" strokeDasharray="4 4" />
                    {minT < 0 && <ReferenceLine y={0} stroke="#2563EB" strokeDasharray="4 4" />}
                    <Line type="monotone" dataKey="temp" name="Temperatura" stroke="#0EA5E9" strokeWidth={1.8} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer></div>
                <p className="mt-1 text-center text-[11px] text-slate-400">Linee tratteggiate: attenzione a 50°C, critico a 60°C</p>
              </div>
            ) : temp != null ? (
              <p className="mt-3 text-center text-[11px] text-slate-400" data-testid="cliente-batteria-temp-vuoto">L'andamento della temperatura si costruisce nei prossimi giorni.</p>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
